import { ContactMoldKind, CountryEnum, MoldConcentration } from '../../enums';
import { AsbestosFinding } from '../../sampleResults/asbestos';
import { DEFAULT_ENGLISH_DATE_FORMAT } from '../../utils/dayjs';

export default {
  language: {
    'de': 'Deutsch',
    'en-US': 'English',
    'nl': 'Nederlands',
  },
  date: {
    format: DEFAULT_ENGLISH_DATE_FORMAT,
  },
  common: {
    back: 'Back',
    logout: 'Logout',
    kcProfile: 'Edit user data',
    downloadPdf: 'Download',
    pageTitles: {
      callback: 'Signing in...',
      refresh: 'Refreshing...',
      activateSampleCode: 'Activate sample code',
      activateSample: 'Activate sample',
      editSenderAddress: 'Edit sender address',
      addSampleComment: 'Add comments to sample',
      generateSampleCodes: 'Generate sample codes',
      accountOverview: 'Account overview',
      resultDetails: 'Result details',
    },
    nav: {
      overview: 'Overview',
      onlineshop: 'Online Shop',
      onlineshopUrl: 'https://ivario-lab.co.uk/',
      faq: 'FAQ & Support',
      faqUrl: 'https://ivario-lab.co.uk/pages/help',
      support: 'Support',
      supportEmail: 'support@ivario.com',
      supportPhone: '+49 (0)40-180 24 111',
      supportPhoneInfo: '(Mon–Fri 9am–5pm)',
      showPhone: 'false',
      myAccount: 'My Account',
      myProfile: 'My Profile',
      myOrders: 'My Orders',
      myOrdersUrl: 'https://account.ivario-lab.co.uk/',
      logOut: 'Log Out',
    },
  },
  errors: {
    emptyValue: 'This field must not be empty.',
    saveAddress:
      'Error saving address or generating label. Please double check your input and try again.',
  },
  component: {
    parameterDetail: {
      oneTab: 'Information',
      twoTab: 'Health',
      threeTab: 'Nutrition',
      fourTab: 'Family',
      noOneInfoAvailable: 'No information is available for this parameter.',
      noTwoInfoAvailable:
        'No health information is available for this parameter.',
      noThreeInfoAvailable:
        'No nutrition information is available for this parameter.',
      noFourInfoAvailable:
        'No family information is available for this parameter.',
    },
    sampleTeaser: {
      activationDate: 'Activation date',
      ACTIVATED: 'Activated',
      ARRIVED_IN_LAB: 'Arrived at the laboratory',
      SHIPPED: 'Shipped by customer',
      DONE: 'Results available',
    },
    shipmentInstructions: {
      shipmentLabelTitle: 'Shipping label for laboratory submission',
      downloadLabel: 'Download shipping label',
      saveQrCode: 'Save QR code',
      shipmentLabelHint:
        '→ Both buttons download the same shipping label - one as a PDF for printing, one as a QR code for your phone (e.g. parcel locker)',
      addAddress: 'Add address',
      foreignShippingInstructions:
        'For information on how your sample should be shipped to our laboratory, please refer to this',
      foreignShippingInstructionsLink: 'FAQ article.',
    },
    sampleDetails: {
      sampleId: 'Sample ID',
      instructions: {
        image: 'Sampling instructions',
        nextSteps: 'Next steps',
        trackingLink: 'Shipment tracking',
        step1: {
          title: 'Sampling',
          point1: 'Open the sample collection kit',
          point2: 'Follow the included manual',
          point3: 'Take the sample according to instructions',
        },
        step2: {
          title: 'Shipping',
          point1: 'Package the sample securely',
          point2: 'Attach the shipping label',
          point3: 'Take the package to the post office',
          point4: 'Send it to our laboratory',
        },
      },
      buttons: {
        downloadLabel: 'Download shipping label',
        downloadCode: 'Download sample code',
      },
      status: {
        title: 'Status',
        ACTIVATED: 'Activated',
        ARRIVED_IN_LAB: 'Arrived at the laboratory',
        SHIPPED: 'Shipped',
        DONE: 'Results available',
        undefined: 'no status',
      },
      unknownField: '-',
      fields: {
        street: 'Street',
        postalCode: 'Postal code',
        region: 'Region',
        houseNumber: 'House number',
        createdAt: 'Created at',
        samplingPerson: 'Sampler',
        address: 'Address',
        samplingLocation: 'Sampling location',
        samplingDate: 'Sampling date',
        samplingTime: 'Sampling time',
        companyName: 'Company name',
        city: 'City',
        notes: 'Notes',
      },
      sampler: 'Sampler',
      edit: 'Edit',
    },
    parameterList: {
      remImageCaption:
        'The image from the scanning electron microscope serves to prove whether asbestos or synthetic mineral fibres has been detected or not. Asbestos fibres generally look scruffy and resemble wool fibres or cotton candy, to put it in layman’s terms. Synthetic mineral fibres look more like thread: They have a narrow core without many fibres branching off from it. At the bottom of the image, you can see the scale given to compare it to the size of the fibre that was detected.',
      edxImageCaption:
        'This is a diagram of energy dispersive X-ray spectroscopy, EDX for short. The material you send in will be examined for the existing elements that react at different energy levels (indicated in KW). The result is compared in the laboratory with the SEM image to confirm whether it is asbestos fibres, synthetic mineral fibres or harmless material. The individual fields in the diagram correspond to the abbreviations for the elements on the periodic table.',
      moreInfoOnParam: 'More information on the parameter',
      errors: {
        noMoldDataAvailable: 'No mold data available',
      },
      asbestosAnalysisError: {
        title: 'Errors during the asbestos analysis',
        description:
          'Please contact our customer service for more information.',
        backToProfile: 'Back to profile',
      },
      moldAnalysisError: {
        title: 'Errors during the mold analysis',
        description:
          'Please contact our customer service for more information.',
        backToProfile: 'Back to profile',
      },
      asbestosDetection: {
        asbestosTitle: 'Asbestos',
        kmfTitle: 'KMF',
        asbestosDetection: 'Asbestos detection',
        kmfDetection: 'KMF detection',
        samplePreparation: 'Sample Preparation',
        procedure: 'Procedure',
        proofThreshold: 'Threshold',
        finding: 'Finding',
      },
      moldValues: {
        [MoldConcentration.NO_MOLD]: 'No mold detected',
        [MoldConcentration.LOW_MOLD]: 'Low mold concentration detected',
        [MoldConcentration.MEDIUM_MOLD]: 'Moderate mold concentration detected',
        [MoldConcentration.HIGH_MOLD]: 'Heavy mold concentration detected',
        NA: 'Sample not analyzable or dried out',
      },
      moldConcentration: {
        [MoldConcentration.NO_MOLD]: 'None',
        [MoldConcentration.LOW_MOLD]: 'Low',
        [MoldConcentration.MEDIUM_MOLD]: 'Moderate',
        [MoldConcentration.HIGH_MOLD]: 'Heavy',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'low colonisation',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'moderate colonisation',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'heavy colonisation',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.BEFALL}`]:
          'low contamination',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.BEFALL}`]:
          'moderate contamination',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.BEFALL}`]:
          'heavy contamination',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.NONE}`]: 'low',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.NONE}`]:
          'moderate',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.NONE}`]: 'heavy',
        name: 'exposure',
      },
      moldExposure: {
        name: 'Exposure',
      },
      keinBefund: 'No findings',
      CFU: 'Colony-forming units (CFUs)',
      moldShortInfo: {
        [MoldConcentration.NO_MOLD]: 'No mold was detected in your sample.',
        [MoldConcentration.LOW_MOLD]:
          'A low concentration of mold was found in your sample.',
        [MoldConcentration.MEDIUM_MOLD]:
          'A moderate concentration of mold was found in your sample.',
        [MoldConcentration.HIGH_MOLD]:
          'A heavy concentration of mold was found in your sample.',
      },
      asbestosValues: {
        [AsbestosFinding.CHRYSOTIL_DETECTED]: 'Chrysotile asbestos detected',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ANTHOPHYLLITE_DETECTED]:
          'Chrysotile and amphibole asbestos (anthophyllite) detected',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_CROCIDOLITE_DETECTED]:
          'Chrysotile and amphibole asbestos (crocidolite) detected',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_AMOSITE_DETECTED]:
          'Chrysotile and amphibole asbestos (amosite) detected',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_TREMOLITE_DETECTED]:
          'Chrysotile and amphibole asbestos (tremolite) detected',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ACTINOLITE_DETECTED]:
          'Chrysotile and amphibole asbestos (actinolite) detected',
        [AsbestosFinding.NO_ASBESTOS_DETECTED]: 'Asbestos not detected',
        [AsbestosFinding.NO_ASBESTOS_INORGANIC_FIBER]:
          'Asbestos not detected (other inorganic fiber)',
        [AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER]:
          'Asbestos not detected (organic fiber)',
        [AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER_ALT]:
          'Asbestos not detected (other fibers detected)',
        [AsbestosFinding.NO_ASBESTOS_TALK_DETECTED]:
          'Asbestos not detected (talc detected)',
        [AsbestosFinding.AMPHIBOLE_TREMOLITE_DETECTED]:
          'Amphibole asbestos (tremolite) detected',
        [AsbestosFinding.AMPHIBOLE_AMOSITE_DETECTED]:
          'Amphibole asbestos (amosite) detected',
        [AsbestosFinding.AMPHIBOLE_ANTHOPHYLLITE_DETECTED]:
          'Amphibole asbestos (anthophyllite) detected',
        [AsbestosFinding.AMPHIBOLE_CROCIDOLITE_DETECTED]:
          'Amphibole asbestos (crocidolite) detected',
        [AsbestosFinding.AMPHIBOLE_ACTINOLITE_DETECTED]:
          'Amphibole asbestos (actinolite) detected',
        [AsbestosFinding.TRACE]: 'Traces',
        [AsbestosFinding.PERCENT_1_TO_5]: '1-5%',
        [AsbestosFinding.PERCENT_5_TO_20]: '5-20%',
        [AsbestosFinding.PERCENT_20_TO_50]: '20-50%',
        [AsbestosFinding.PERCENT_OVER_50]: '> 50%',
        [AsbestosFinding.NOT_POSSIBLE]: '',
        [AsbestosFinding.MINUS]: '',
        [AsbestosFinding.AMF_NOT_DETECTED]:
          'Artificial mineral fibers not detected',
        [AsbestosFinding.AMF_DETECTED_NON_WHO]:
          'Artificial mineral fibers detected (non-WHO fibers)',
        [AsbestosFinding.AMF_DETECTED_WHO]:
          'Artificial mineral fibers detected (WHO fibers)',
      },
      notAnalyzable: 'NA',
      notAnalyzableLong: 'Not analyzable',
      notDetectable: 'ND',
      notDetectableLong: 'Not detectable',
      exceededRegulation: 'Limit exceeded',
      closeToExceedingRegulation: 'Close to limit',
      parameter: 'Parameter',
      value: 'Value',
      evaluation: 'Evaluation',
      status: 'Status',
      details: 'Show details',
      recommendations: 'Recommendations',
      statusTypes: {
        error: 'Problematic',
        warning: 'At the limit',
        success: 'Normal',
        unset: 'Unset',
        notAnalyzable: 'Not Analyzable',
      },
      sort: {
        label: 'Sort by',
        CATEGORY_ASC: 'Parameter group (A-Z)',
        CATEGORY_DESC: 'Parameter group (Z-A)',
        ALPHABET_ASC: 'Alphabetical (A-Z)',
        ALPHABET_DESC: 'Alphabetical (Z-A)',
        DEVIATION_ASC: 'Deviation from limit (ascending)',
        DEVIATION_DESC: 'Deviation from limit (descending)',
      },
    },
    impersonationBanner: {
      message:
        'You are on a customer support view-only screen, some functionality, like editing sample data, will not work here',
    },
    announcementBar: {
      readMore: 'Read more',
      readLess: 'Read less',
    },
  },
  view: {
    commentsForm: {
      title: 'Add comments to your sample',
      description: 'This data will be visible in your analysis results.',
      samplingPoint: 'Sampling location',
      companyName: 'Company name',
      street: 'Street',
      houseNumber: 'House number',
      postalCode: 'Postal code',
      city: 'City',
      samplingDate: 'Sampling date',
      samplingTime: 'Sampling time',
      sampler: 'Sampler',
      notes: 'Notes',
      saveButton: 'Save comments',
      back: '$t(common:back)',
    },
    error: {
      apology: 'Sorry, an error occurred.',
      details: '{{stack}}',
      buttons: {
        backToProfile: 'Back to profile overview',
        logout: '$t(common:logout)',
      },
      empty: 'Input must not be empty.',
      connectionError: 'Connection error',
      503: 'Service Unavailable',
    },
    profile: {
      welcome: 'Welcome, {{name}}!',
      addSample: 'Add Sample Code',
      title: 'All samples',
      result: 'Result',
      details: 'Details',
      latestSample: {
        nextStep: 'Next steps for the sample',
        title: 'Send sample now: {{productName}}',
        infoText:
          'Your sample has been successfully activated. Next, take a sample as described in the package insert and send it to our laboratory.',
      },
    },
    registerSample: {
      title: 'Activate sample code',
      subtitle:
        'Enter your sample code to activate your analysis and proceed with the submission.',
      hintTitle: 'Where can I find my sample code?',
      hintDescription:
        'It can be found on the first page of the enclosed instructions.',
      code: 'Sample code',
      codePlaceholder: 'e.g. sabu3-xuwe2-vafu2',
      inputNote: 'Case sensitive',
      verify: 'Continue',
      invalidCode: 'Invalid sample code',
      cancelledCode:
        'This sample code can not be activated. Please contact support if you have any questions.',
      empty: '$t(view:error.empty)',
    },
    landing: {
      tabTitle: 'IVARIO Login',
      metaDescription:
        'View the lab results of your water analysis or asbestos test securely and quickly online. Log in to your IVARIO customer account now!',
      pageTitle: 'Activate Your Sample',
      pageSub: 'Register or log in to submit your sample.',
      newUser: {
        title: "I'm new here",
        description:
          "Don't have an account yet? Even if you've ordered from us before, you must register in the Analyses Report Center.",
        nextStepsLabel: 'What happens next:',
        steps: [
          'Confirm email address',
          'Enter sample code',
          'Create shipping label',
          'Receive results',
        ],
        cta: 'Register now',
      },
      existingUser: {
        title: 'I already have an account',
        description:
          'I already have an account in the IVARIO Analyses Report Center.',
        info: [
          'View & manage samples',
          'Download analysis report',
          'Create shipping label',
        ],
        cta: 'Login',
      },
      oldResults:
        'Are you looking for analysis results from samples taken before October 2025?',
      helpPrefix: 'Questions?',
      helpLink: 'Contact customer service',
      supportUrl: 'https://support.ivario.eu/en/support/home',
      headerLangLabel: 'Language:',
      overlay: {
        title: 'Language detected',
        sub: 'We detected <strong>English</strong> as your web browser language.<br>Would you like to continue in English?',
        confirm: 'Continue in English',
        or: 'or choose a different language',
        otherLabel: 'Other language:',
        selectBtn: 'Continue with selected language',
      },
    },
    senderAddress: {
      title: 'New Sample',
      success: 'Successfully registered',
      failure: 'Registration failed',
      save: 'Save',
    },
    result: {
      title: 'Analysis result: {{id}}',
      loading: 'Loading result...',
    },
    internal: {
      generateSampleCodes: {
        title: 'Generate sample codes',
        selectProduct: 'Select a product',
        numberOfCodes: 'How many sample codes would you like to generate?',
        generatedFor: 'Generated for',
        generatedForPlaceholder: 'e.g. Obi, Batch 1002, Trade fair Hannover',
        generate: 'Generate sample codes',
        close: 'Close',
        error: 'Error generating sample codes',
        minimumCodes: 'The number of codes must be at least one.',
        generatedForRequired:
          'Please enter at least five characters for what the codes are generated for.',
      },
    },
    footer: {
      shipping: 'Shipping and payment',
      dataPrivacy: 'Data protection',
      blog: 'IVARIO blog',
      experience: 'Customer reviews',
      testResult: 'To the legacy system before October 2025.',
      agb: 'General terms and conditions and revocation',
      faq: 'FAQ and contact',
      waterWiki: 'Drinking water blog',
      imprint: 'Imprint',
      testCompare: 'Water test comparison',
      moldWiki: 'Mold blog',
      contact: 'Contact',
      accreditation: 'Accreditation',
      affiliate: 'Affiliate partners',
      asbestos: 'Asbestos consultant',
      agbUrl: 'https://ivario.com/en-uk/policies/terms-of-service',
      dataPrivacyUrl: 'https://ivario.com/en-uk/policies/privacy-policy',
    },
    senderAddressForm: {
      title: 'Add sender address',
      subtitle:
        'To create a shipping label, a sender address must first be added. Free shipping labels can only be created for Germany, Austria and the Netherlands.',
      lastName: 'Last Name',
      firstName: 'First Name',
      street: 'Street',
      houseNumber: 'House Number',
      postalCode: 'Postal Code',
      city: 'City',
      country: 'Country',
      companyName: 'Company Name (optional)',
      saveSenderAddress: 'Save Sender Address',
    },
  },
  countries: {
    [CountryEnum.Germany]: 'Germany',
    [CountryEnum.Austria]: 'Austria',
    [CountryEnum.Belgium]: 'Belgium',
    [CountryEnum.Bulgaria]: 'Bulgaria',
    [CountryEnum.Croatia]: 'Croatia',
    [CountryEnum.Cyprus]: 'Cyprus',
    [CountryEnum['Czech Republic']]: 'Czech Republic',
    [CountryEnum.Denmark]: 'Denmark',
    [CountryEnum.Estonia]: 'Estonia',
    [CountryEnum.Finland]: 'Finland',
    [CountryEnum.France]: 'France',
    [CountryEnum.Greece]: 'Greece',
    [CountryEnum.Hungary]: 'Hungary',
    [CountryEnum.Ireland]: 'Ireland',
    [CountryEnum.Italy]: 'Italy',
    [CountryEnum.Latvia]: 'Latvia',
    [CountryEnum.Lithuania]: 'Lithuania',
    [CountryEnum.Luxembourg]: 'Luxembourg',
    [CountryEnum.Malta]: 'Malta',
    [CountryEnum.Netherlands]: 'Netherlands',
    [CountryEnum.Poland]: 'Poland',
    [CountryEnum.Portugal]: 'Portugal',
    [CountryEnum.Romania]: 'Romania',
    [CountryEnum.Slovakia]: 'Slovakia',
    [CountryEnum.Slovenia]: 'Slovenia',
    [CountryEnum.Spain]: 'Spain',
    [CountryEnum.Sweden]: 'Sweden',
    [CountryEnum.Switzerland]: 'Switzerland',
  },
  toasts: {
    sampleClaimed: {
      title: 'Successfully activated',
      subTitle: 'Your sample has been successfully activated',
    },
    sampleClaimedFailed: {
      title: 'Error',
      subTitle: 'Your sample could not be activated',
    },
  },
  legacy: {
    register: 'Have you already activated the sample in the legacy system? ',
    registerLink: 'Log in here\u00A0→',
    sampleList:
      'If you have an old account with IVARIO (created before October 2025), you can find our legacy system ',
    sampleListLink: 'here',
    sampleListOutro: ' to view your registered samples and analysis reports.',
  },
} as const;
